import {
  FC,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { clamp } from '../../lib/clamp';

export interface IHueSliderProps {
  /**
   * Fired when the user changes the hue
   */
  onChangeHue: (value: number) => void;

  /**
   * The current hue
   */
  hue: number;
}

/**
 * This component expects its value to be controlled from the parent component
 */
export const HueSlider: FC<IHueSliderProps> = ({ onChangeHue, hue }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSliding, setIsSliding] = useState<boolean>(false);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      if (e.button === 0 && ref.current) {
        setIsSliding(true);
        onChangeHue(determineHue(e.clientY, ref.current));
      }
    },
    [onChangeHue],
  );

  useEffect(() => {
    if (!isSliding) {
      return;
    }
    function onPointerMove(e: PointerEvent) {
      if (ref.current) {
        onChangeHue(determineHue(e.clientY, ref.current));
      }
    }
    function onPointerUp() {
      setIsSliding(false);
    }
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [isSliding, onChangeHue]);

  return (
    <div ref={ref} className="hue-slider" onPointerDown={onPointerDown}>
      <div className="indicator" style={{ top: `${hue * 100}%` }} />
    </div>
  );
};

/**
 * Returns a value from 0 to 1 based on the pointer's y coordinate (clientY) and the slider's element position.
 */
function determineHue(pointerY: number, elem: HTMLElement): number {
  if (!elem) {
    return 0;
  }
  const rect = elem.getBoundingClientRect();
  return clamp(pointerY - rect.y, 0, rect.height) / rect.height;
}
